import { forwardRef } from 'react';
import type { FieldBaseProps } from './FieldBase';
import { FieldBase } from './FieldBase';

export interface FieldProps
  extends Omit<FieldBaseProps, 'title' | 'labelType' | 'inputId'> {
  /**
   * Label of the field.
   */
  labelText: string;
  /**
   * `id` of the child input.
   * Will be added as the `for` attribute of the label.
   */
  inputId: string;
}

/**
 * Field component
 *
 * {@link https://zeroheight.com/76c439891/p/368591-forms Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-field--basic-field Code documentation}
 *
 * @example
 * <Field labelText="My label" inputId="my-input">
 *  <TextInput type="text" id="my-input" />
 * </Field>
 */
export const Field = forwardRef<HTMLDivElement, FieldProps>(
  (
    {
      inputId,
      helperTextId,
      invalidTextId,
      labelText,
      children,
      ...props
    }: FieldProps,
    ref,
  ) => {
    helperTextId = helperTextId || `${inputId}-helper`;
    invalidTextId = invalidTextId || `${inputId}-invalid`;

    return (
      <FieldBase
        ref={ref}
        title={labelText}
        titleAs="label"
        helperTextId={helperTextId}
        invalidTextId={invalidTextId}
        inputId={inputId}
        {...props}
      >
        {children}
      </FieldBase>
    );
  },
);

Field.displayName = 'Field';

Field.defaultProps = {
  isRequired: false,
  isInvalid: false,
  titleAs: 'label',
  size: 'm',
};
